import React, { useRef, useState, useEffect } from 'react'
import { useIsIntersecting } from 'utils/hooks/useIsIntersecting'
import { useWindowSize } from 'utils/hooks'
import styles from './IconButtonsCard.module.scss'
import cx from 'classnames'
import { AssetType, IconButtonsCardProps } from '~types'
import Image from 'next/image'
import { Heading } from '~elements'
import { getImageUrl } from 'utils/assets/getImageUrl'
import Video from 'components/elements/Video/Video'
import { hexToRgb } from '~utils/styles/hexToRgb'
import {
  ColorPaletteBrandPrimary100,
  ColorPaletteBrandPrimary600,
  ColorAliasNeutralBlack
} from '~styles/dist/tokens.mjs'

const SVGIcon = ({ id, url, width, height }: { id: string; url: string; width: string; height: string }) => (
  <svg width={width} height={height} className={cx(styles['icon'], styles['delay-short'])}>
    <defs>
      <linearGradient id={`defaultGradient-${id}`} x1='0%' y1='0%' x2='100%' y2='100%'>
        <stop offset='0%' stopColor={`rgba(${hexToRgb(ColorPaletteBrandPrimary600)}, 1)`} stopOpacity='1' />
        <stop offset='100%' stopColor={`rgba(${hexToRgb(ColorPaletteBrandPrimary100)},1)`} stopOpacity='1' />
      </linearGradient>

      <filter id={`dropShadow-${id}`} x='-20%' y='-20%' width='140%' height='140%'>
        <feDropShadow
          dx='0'
          dy='0'
          stdDeviation='8'
          floodColor={`rgba(${hexToRgb(ColorAliasNeutralBlack)}, 1)`}
          floodOpacity='0.5'
        />
      </filter>

      <mask id={`iconMask-${id}`}>
        <rect width={width} height={height} fill={ColorAliasNeutralBlack} />
        <image width={width} height={height} href={url} style={{ filter: 'brightness(0) invert(1)' }} />
      </mask>
    </defs>

    <rect
      width={width}
      height={height}
      fill={`url(#defaultGradient-${id})`}
      filter={`url(#dropShadow-${id})`}
      mask={`url(#iconMask-${id})`}
    />
  </svg>
)

const IconButtonsCard = ({
  eyebrow,
  headline,
  backgroundImage,
  backgroundVideo,
  iconButtons
}: IconButtonsCardProps) => {
  const { isBreakpoint: isBelowBreakpointMd } = useWindowSize('md', '<')
  const { isBreakpoint: isBelowBreakpointSm } = useWindowSize('sm', '<')
  const backgroundImageUrl = getImageUrl({ imageUrl: backgroundImage?.url })
  const hoverRef = useRef(null)
  const [hasAnimationPlayed, setHasAnimationPlayed] = useState(false)
  const isElementIntersecting = useIsIntersecting({
    ref: hoverRef,
    observerOptions: {
      threshold: [0.2]
    }
  })

  useEffect(() => {
    if (isElementIntersecting && !hasAnimationPlayed) {
      setHasAnimationPlayed(true)
    }
  }, [isElementIntersecting, hasAnimationPlayed])
  const enabledButtonsCount = iconButtons.filter(btn => btn.enable).length

  const getDelayClass = (index: number) => {
    if (enabledButtonsCount >= 7 && index >= 4) return styles['delay-long']
    if (enabledButtonsCount <= 6 && index >= 3) return styles['delay-long']
    if (enabledButtonsCount === 4) return styles['delay-long']

    return styles['delay-short']
  }

  const getLayoutClass = (numButtons: number) => {
    if (isBelowBreakpointMd) {
      return styles['two-per-row']
    }
    if (numButtons >= 7 || numButtons === 4) {
      return styles['four-per-row']
    }
    return styles['three-per-row']
  }
  const layoutClass = getLayoutClass(enabledButtonsCount)

  return (
    <div className={styles['icon-buttons-card']} ref={hoverRef}>
      {backgroundVideo?.url ? (
        <Video
          autoPlay
          videoLoop
          backgroundVideoXl={{ url: backgroundVideo?.url, fileName: 'video file' }}
          posterImageXl={{ url: backgroundImageUrl } as AssetType}
        />
      ) : (
        backgroundImageUrl && (
          <Image
            src={backgroundImageUrl}
            alt={backgroundImage?.altText || headline || ''}
            fill
            style={{ objectFit: 'cover', objectPosition: 'top' }}
          />
        )
      )}
      <div className={styles['content-wrapper']}>
        {eyebrow && (
          <div className={cx(styles['eyebrow'], hasAnimationPlayed ? styles['animate'] : '')}>
            <Heading level={6} color='primary-light' justify='center'>
              {eyebrow}
            </Heading>
          </div>
        )}
        {headline && (
          <div
            className={cx(styles['headline'], hasAnimationPlayed ? cx(styles['animate'], styles['delay-short']) : '')}
          >
            <Heading level={2} justify='center' noMarginBottom>
              {headline}
            </Heading>
          </div>
        )}
        <div className={styles['cards-container']}>
          {iconButtons.map(
            (btn, index) =>
              btn.enable && (
                <div
                  key={btn.id}
                  className={cx(styles['icon-button'], layoutClass, hasAnimationPlayed ? styles['animate'] : '')}
                >
                  <a href={btn.url} rel='noreferrer' target={btn.openLinkInNewTab ? '_blank' : '_self'}>
                    <div
                      className={cx(
                        styles['hover-container'],
                        hasAnimationPlayed ? styles['animate'] : '',
                        getDelayClass(index)
                      )}
                    >
                      {btn?.icon?.url && (
                        <SVGIcon
                          key={btn.id}
                          id={btn.id}
                          url={btn.icon.url}
                          width={isBelowBreakpointSm ? '40' : '80'}
                          height={isBelowBreakpointSm ? '40' : '80'}
                        />
                      )}
                      <span
                        className={cx(
                          styles['text'],
                          hasAnimationPlayed ? styles['animate'] : '',
                          styles['delay-long']
                        )}
                      >
                        {btn.cta}
                      </span>
                    </div>
                  </a>
                </div>
              )
          )}
        </div>
      </div>
    </div>
  )
}

export default IconButtonsCard
